import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { closeModal, openModal } from "../../../redux/modal/modalActions";
import ResponseModal from "../../../components/common/modal/responseModal/ResponseModal";
import ConfirmationBox from "../../../components/common/confirmationBox/ConfirmationBox";
import Table from "../../../components/common/table/Table";
import { TableLinkCell, TableTextCell, TaskStatusCheckboxCell } from "../../../components/common/table/TableDisplayCells";
import { TaskPriorityChip } from "../../../components/common/taskPriority/TaskPriorityChip";
import { openTaskDetailModal } from "../../../components/propertyTasks/TaskDetailModalContent";
import { TaskComposerModalContent } from "../../../components/propertyTasks/PropertyTasksSection";
import { updateTask } from "../../../apis/tasksApis/updateTask";
import { deleteTask } from "../../../apis/tasksApis/deleteTask";
import { mapAssignedTaskRowsGroupedByProperty } from "../../../helpers/homepageAssignedTasksHelper";
import { createMenuItem } from "../../../helpers/menuHelper";
import {
  buildTaskStatusUpdatePayload,
  resolveTaskForEditComposer,
  resolveTaskForStatusUpdate,
} from "../../../helpers/taskUpdateHelper";
import { showTaskStatusErrorModal, showTaskStatusSuccessModal } from "../../../helpers/taskStatusFeedback";
import { getCookie } from "../../../utils/cookies";
import { useAuth } from "../../../auth/hooks/usePermissions";
import { useTaskPermissions } from "../../../auth/hooks/useTaskPermissions";
import { isAdminRole } from "../../../auth/utils/roles";
import { HOMEPAGE_ASSIGNED_TASKS_TABLE_EMPTY } from "../../../consts/tableEmptyMessages";
import { useAssignedTasksHomepage } from "./useAssignedTasksHomepage";
import "../../../components/propertyTasks/PropertyTasksSection.css";
import "../Homepage.css";
import "./DisplayAssignedTasks.css";

function buildAssignedTaskTableHeaders({ showAssignedTo, canUpdateTasks, busyTaskId, onStatusCheckbox, onTaskRowClick }) {
  const headers = [
    {
      key: "completed_sort",
      name: "Status",
      formatter: (row) => {
        const task = row._task;
        const busyRow = busyTaskId === task.id;
        return (
          <TaskStatusCheckboxCell
            task={task}
            checked={task.status === "completed"}
            disabled={!canUpdateTasks || busyRow}
            onChange={(e) => onStatusCheckbox(task, e.target.checked)}
          />
        );
      },
    },
    {
      key: "priority_sort",
      name: "Priority",
      formatter: (row) => (
        <TaskPriorityChip
          priority={row._task.priority}
          className="task-priority-chip--compact"
        />
      ),
    },
    {
      key: "property_address_display",
      name: "Full address of property",
      formatter: (row) => (
        <TableTextCell text={row.property_address_display} className="table-display-text-cell display-assigned-tasks-text-cell--address" />
      ),
    },
    {
      key: "description_display",
      name: "Description",
      formatter: (row) => (
        <TableTextCell
          text={row._task.description}
          className="property-tasks-table-text-cell"
          wrapClassName="property-tasks-tooltip-wrap"
          tooltipContentClassName="property-tasks-table-tooltip-content"
          tooltipClassName="property-tasks-table-tooltip"
          buttonClassName="property-tasks-desc-btn"
          asButton
          onClick={(e) => {
            e.stopPropagation();
            onTaskRowClick(row._task);
          }}
        />
      ),
    },
  ];

  if (showAssignedTo) {
    headers.push({ key: "assigned_to_display", name: "Assigned to" });
  }

  headers.push(
    { key: "start_date_display", name: "Start date" },
    {
      key: "link_raw",
      name: "Link",
      formatter: (row) => <TableLinkCell url={row.link_raw} onClick={(e) => e.stopPropagation()} />,
    },
    {
      key: "comment_display",
      name: "Comment",
      formatter: (row) => <TableTextCell text={row._task.comment} />,
    },
    { key: "created_by_display", name: "Created by" },
  );

  return headers;
}

const VARIANT_CONFIG = {
  active: {
    title: "Active Tasks : ",
    titleClassName: "strong-margin green",
    emptyMessage: HOMEPAGE_ASSIGNED_TASKS_TABLE_EMPTY.ACTIVE,
  },
  finished: {
    title: "Finished Tasks : ",
    titleClassName: "strong-margin red",
    emptyMessage: HOMEPAGE_ASSIGNED_TASKS_TABLE_EMPTY.FINISHED,
  },
};

function DisplayAssignedTasks({ homePageDisplay, variant }) {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useAuth();
  const { canUpdateTasks, canDeleteTasks } = useTaskPermissions();
  const showAssignedToColumn = isAdminRole(user?.role);
  const [busyTaskId, setBusyTaskId] = useState(null);

  const onFetchError = useCallback(
    (message) => {
      dispatch(openModal(<ResponseModal text={message ?? "Could not load assigned tasks."} isError={true} />));
    },
    [dispatch],
  );

  const { activeTasks, finishedTasks, loading, loadAssignedTasks } = useAssignedTasksHomepage(isAuthenticated, onFetchError);

  const rows = variant === "finished" ? finishedTasks : activeTasks;
  const { title, titleClassName, emptyMessage } = VARIANT_CONFIG[variant] ?? VARIANT_CONFIG.active;

  const handleStatusCheckbox = useCallback(
    async (task, checked) => {
      if (!canUpdateTasks) return;

      const uid = getCookie("userId");
      if (!uid || Number.isNaN(Number(uid))) {
        showTaskStatusErrorModal(dispatch, "Missing session user ID. Sign in again.");
        return;
      }

      const next = checked ? "completed" : "not_completed";
      setBusyTaskId(task.id);
      try {
        const { task: taskForUpdate, errorMessage } = await resolveTaskForStatusUpdate(task);
        if (errorMessage || !taskForUpdate) {
          showTaskStatusErrorModal(dispatch, errorMessage ?? "Could not load task details.");
          return;
        }

        const res = await updateTask(buildTaskStatusUpdatePayload(taskForUpdate, checked, uid));
        if (res.error_code !== 1) {
          showTaskStatusErrorModal(dispatch, res.error_message ?? "Could not update status.");
          return;
        }
        await loadAssignedTasks({ showSpinner: false });
        dispatch(closeModal());
        showTaskStatusSuccessModal(dispatch, next === "completed");
      } finally {
        setBusyTaskId(null);
      }
    },
    [canUpdateTasks, dispatch, loadAssignedTasks],
  );

  const executeDeleteTask = useCallback(
    async (taskId) => {
      if (!canDeleteTasks) return;
      setBusyTaskId(taskId);
      try {
        const res = await deleteTask(taskId);
        if (res.error_code !== 1) {
          dispatch(
            openModal(
              <ResponseModal
                text={res.error_message ?? "Could not delete task."}
                isError={true}
              />,
            ),
          );
          return;
        }
        await loadAssignedTasks({ showSpinner: false });
        dispatch(closeModal());
      } finally {
        setBusyTaskId(null);
      }
    },
    [canDeleteTasks, dispatch, loadAssignedTasks],
  );

  const confirmDeleteTask = useCallback(
    (taskId) => {
      if (!canDeleteTasks) return;
      dispatch(
        openModal(
          <ConfirmationBox
            msg="Are you sure you want to delete the task?"
            saveText="Delete"
            discardText="Cancel"
            saveAction={() => {
              void executeDeleteTask(taskId);
            }}
          />,
        ),
      );
    },
    [canDeleteTasks, dispatch, executeDeleteTask],
  );

  const openEditTaskModal = useCallback(
    async (task) => {
      if (!canUpdateTasks) return;

      const { task: taskForEdit, propertyId, propertyTypeId, errorMessage } =
        await resolveTaskForEditComposer(task);
      if (errorMessage || !taskForEdit || propertyId == null) {
        dispatch(
          openModal(
            <ResponseModal
              text={errorMessage ?? "Could not open task for editing."}
              isError={true}
            />,
          ),
        );
        return;
      }

      dispatch(
        openModal(
          <TaskComposerModalContent
            dispatch={dispatch}
            mode="edit"
            task={taskForEdit}
            propertyId={propertyId}
            propertyTypeId={propertyTypeId}
            refetchTasks={() => loadAssignedTasks({ showSpinner: false })}
            setBusyTaskId={setBusyTaskId}
          />,
        ),
      );
    },
    [canUpdateTasks, dispatch, loadAssignedTasks],
  );

  const handleTaskRowClick = useCallback(
    (task) => {
      openTaskDetailModal(dispatch, {
        task,
        canUpdateTasks,
        canDeleteTasks,
        busyTaskId,
        onEdit: canUpdateTasks ? () => openEditTaskModal(task) : undefined,
        onConfirmDelete: canDeleteTasks ? () => confirmDeleteTask(task.id) : undefined,
        onStatusCheckbox: handleStatusCheckbox,
        showPropertyAddress: true,
      });
    },
    [
      busyTaskId,
      canDeleteTasks,
      canUpdateTasks,
      confirmDeleteTask,
      dispatch,
      handleStatusCheckbox,
      openEditTaskModal,
    ],
  );

  const rowActions = useMemo(() => {
    const actions = [
      canUpdateTasks &&
        createMenuItem("Edit", (row) => openEditTaskModal(row._task), <EditIcon />),
      canDeleteTasks &&
        createMenuItem("Delete", (row) => confirmDeleteTask(row._task.id), <DeleteIcon />),
    ].filter(Boolean);
    return actions.length > 0 ? actions : undefined;
  }, [canDeleteTasks, canUpdateTasks, confirmDeleteTask, openEditTaskModal]);

  const tableRows = useMemo(() => mapAssignedTaskRowsGroupedByProperty(rows), [rows]);

  const headers = useMemo(
    () =>
      buildAssignedTaskTableHeaders({
        showAssignedTo: showAssignedToColumn,
        canUpdateTasks,
        busyTaskId,
        onStatusCheckbox: handleStatusCheckbox,
        onTaskRowClick: handleTaskRowClick,
      }),
    [showAssignedToColumn, canUpdateTasks, busyTaskId, handleStatusCheckbox, handleTaskRowClick],
  );

  if (!homePageDisplay || !isAuthenticated) {
    return null;
  }

  return (
    <div className="homepage-table-section">
      <strong className={titleClassName}>{title}</strong>
      <div className="homepage-table-card">
        {loading ? (
          <div className="display-assigned-tasks-spinner" role="status">
            <CircularProgress size={36} thickness={4} />
          </div>
        ) : (
          <Table
            tableHeight="450px"
            headers={headers}
            rows={tableRows}
            searchable={false}
            rowActions={rowActions}
            onRowClick={(row) => handleTaskRowClick(row._task)}
            emptyMessage={emptyMessage}
          />
        )}
      </div>
    </div>
  );
}

DisplayAssignedTasks.propTypes = {
  homePageDisplay: PropTypes.bool,
  variant: PropTypes.oneOf(["active", "finished"]).isRequired,
};

DisplayAssignedTasks.defaultProps = {
  homePageDisplay: false,
};

export default DisplayAssignedTasks;
